import React, {
  useEffect,
  useMemo,
  useCallback,
  useState,
  useRef,
  Fragment,
} from 'react';
import { Project as ProjectModel } from '@material-bank/projects-api';
import * as projectsEditDialogConfig from '@material-bank/projects-edit-dialog-config';
import {
  useCurrentProject,
  useProjectsProvider,
  useValidateProjectData,
} from '@material-bank/projects-utils';
import { AnalyticsEventLocation, AnalyticsEventName } from '@mb/lib';
import { getUserLang } from '@mb/lib';
import { Pagination } from '@mui/material';
import { useScreenSize } from '@productSearch/hooks/useScreenSize';
import { StatusEnum as SnackbarStatusEnum } from 'components/components/MBSnackbar';
import debounce from 'lodash/debounce';
import { useIntl } from 'react-intl';
import { useSelector, useDispatch } from 'react-redux';
import { VirtuosoGrid } from 'react-virtuoso';

import { ItemContainer } from './ItemContainer';
import * as Styled from './MaterialsContainer.styles';
import { ResultCardWrapper } from './ResultCardWrapper';
import { sendAnalytics } from '../../api/events';
import { CampaignBanner } from '../../components/CampaignBanner';
import { ProductCartClickProps } from '../../components/Cards/ResultCard/types';
import { EmptyResult } from '../../components/EmptyResult';
import { GridSkeleton } from '../../components/GridSkeleton';

import { config } from '../../config';
import { useSearchContext } from '../../contexts';
import {
  getEventsMaterialProps,
  pushCompareAnalyticsEvent,
} from '../../helpers';
import useAddToBagWithValidations from '../../hooks/useAddToBagWithValidations';
import { usePrevious } from '../../hooks/usePrevious';
import { useViewScrollableProductsListEvent } from '../../hooks/useViewScrollableProductsListEvent';
import { pushColorwaysClickAnalytics } from '../../pushColorwaysClickAnalytics';
import {
  cartItemsSelector,
  comparisonItemsSelector,
  isComparisonActiveSelector,
  isSearchLiteSelector,
} from '../../redux/selectors/commonSelector';
import {
  multiSelectToggleSelector,
  selectedMaterialsSelector,
  searchToggleSelector,
  allCurrentPrimaryColorsLabelsSelector,
  appFiltersSelector,
} from '../../redux/selectors/filtersSelector';
import {
  searchPaginationSelector,
  searchOptionsSelector,
  isSearchingSelector,
  searchResultsSelector,
  campaignFooterBannerSelector,
  campaignContentBannerSelector,
  isSearchRedirectSelector,
  selectedVariantsSelector,
  xRequestIdSelector,
} from '../../redux/selectors/searchSelector';
import {
  setSnackbarInfo,
  removeCartItemFromQueue,
  updateComparisonItems,
} from '../../redux/sliceCreators/commonSlice';
import {
  setMultiSelectToggle,
  toggleSelectedMaterial,
} from '../../redux/sliceCreators/filtersSlice';
import {
  setIsProjectModalOpen,
  setProjectModalEventLocation,
} from '../../redux/sliceCreators/projectsSlice';
import { setSelectedVariants } from '../../redux/sliceCreators/searchSlice';
import { CartItem, ProductSearchMaterial } from '../../types';
import { areComponentPropsEqual } from '../../utils';
import {
  isSearchPage,
  isCuratedCollectionPage,
  isBannersPage,
  isPartnersPage,
  pushSearchAnalyticsDataLayer,
  getTaxonomyAnalyticsDataFromFilters,
  handleAddToBoards,
} from '../../utils/helpers';
import { getProductCardLabels } from '../SearchResultsContainer/utilities/getProductCardLabels';

const isSearch = isSearchPage();
const isBannersSearch = isBannersPage();
const isPartnersSearch = isPartnersPage();
const userLang = getUserLang();

export const activeBagTime = 2500;
const defaultImageSize = 252;

const MaterialsContainer = React.memo(() => {
  const dispatch = useDispatch();
  const results = useSelector(searchResultsSelector);
  const pagination = useSelector(searchPaginationSelector);
  const searchOptions = useSelector(searchOptionsSelector);
  const campaignContentBanner = useSelector(campaignContentBannerSelector);
  const { handleNextPage } = useSearchContext();
  const selectedMaterials = useSelector(selectedMaterialsSelector);
  const comparisonItems = useSelector(comparisonItemsSelector);
  const searchToggle = useSelector(searchToggleSelector);
  const isMultiselect = useSelector(multiSelectToggleSelector);
  const isSearchLite = useSelector(isSearchLiteSelector);
  const xRequestId = useSelector(xRequestIdSelector);
  const currentSelectedColors = useSelector(
    allCurrentPrimaryColorsLabelsSelector,
  );
  const appFilters = useSelector(appFiltersSelector);
  const isSearchRedirect = useSelector(isSearchRedirectSelector);

  const { currentProject, setCurrentProjectId } = useCurrentProject();

  const { activeProjects } = useProjectsProvider();
  const { isProjectDataComplete } = useValidateProjectData();

  const isSearching = useSelector(isSearchingSelector);
  const campaignBottomBanner = useSelector(campaignFooterBannerSelector);
  const cartItems = useSelector(cartItemsSelector);
  const selectedVariants = useSelector(selectedVariantsSelector);
  const isComparisonActive = useSelector(isComparisonActiveSelector);
  const previousResultsLength = usePrevious(results?.length);
  const [isPageZoomed, setIsPageZoomed] = useState(
    window.devicePixelRatio !== 1,
  );
  const intl = useIntl();
  const { isMobile, isSmallDesktop } = useScreenSize();

  const { handleAddToBag, loadingIds } = useAddToBagWithValidations();

  const cardStaticLabels = useMemo(() => {
    return getProductCardLabels(intl);
  }, [intl]);

  const gridContainerRef = useRef<HTMLDivElement>(null);
  const resultContainerRef = useRef<HTMLDivElement>(null);

  const multiselectIds = useMemo(() => {
    const relevantList = isComparisonActive
      ? comparisonItems
      : selectedMaterials;
    return (relevantList ?? []).map(
      (item: ProductSearchMaterial) => item.entityId,
    );
  }, [selectedMaterials, comparisonItems, isComparisonActive]);

  const currentProjectData = {
    ...currentProject,
    id: currentProject?.externalId ?? 0,
    label: currentProject?.title ?? 'Select project..',
  };

  const structuredProjects = useMemo(() => {
    return Object.values(activeProjects).map((project: ProjectModel) => ({
      ...project,
      id: project.externalId,
      label: project.title,
    }));
  }, [activeProjects]);

  useEffect(() => {
    listenOnDevicePixelRatio();
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // If something was added to Cart, show isAdded icon for X secconds,
  // and then remove icon
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      const oldCartItems = cartItems?.filter(
        (item: CartItem) => now - item.time >= activeBagTime,
      );
      if (oldCartItems?.length > 0) {
        dispatch(removeCartItemFromQueue(oldCartItems));
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [cartItems]);

  useEffect(() => {
    //do this only on mount, when coming back from PDP
    if (results?.length > 0 && previousResultsLength === 0) {
      const ssScroll = sessionStorage.getItem('ssScroll');
      const prevUrl = sessionStorage.getItem('prevSearchUrl');
      const currentUrl = window.location.href;
      if (ssScroll != null && prevUrl != null && prevUrl === currentUrl) {
        setTimeout(() => {
          window.scrollTo(0, Number(ssScroll));
        }, 500);
      }
    }
  }, [results?.length]);

  // Appendix for clients, when zooming in browser Search page. On some zoom steps -
  // grid starts to bounce (Virtuoso calc issue). Listener when Zoom ratio is changed.
  function listenOnDevicePixelRatio() {
    function onChange() {
      setIsPageZoomed(window.devicePixelRatio !== 1);
      listenOnDevicePixelRatio();
    }
    matchMedia(`(resolution: ${window.devicePixelRatio}dppx)`).addEventListener(
      'change',
      onChange,
      { once: true },
    );
  }

  const handleMaterialSelect = (product: ProductSearchMaterial) => {
    const isMultiSelected =
      (multiselectIds ?? []).find((id: string) => id === product?.entityId) !=
      null;

    if (isComparisonActive) {
      pushCompareAnalyticsEvent({
        product,
        eventName: isMultiSelected
          ? 'comparison_tool_item_checkbox_deselected'
          : 'comparison_tool_item_checkbox_selected',
        withProductData: true,
        projectData: {
          name: currentProjectData?.label,
          id: String(currentProjectData?.id),
        },
      });
      dispatch(updateComparisonItems(product));
      return;
    }
    if (!isMultiselect) {
      dispatch(setMultiSelectToggle(true));
    }
    if (
      selectedMaterials?.length >= config.maxSelectNumber &&
      selectedMaterials?.find(
        (item: ProductSearchMaterial) => item.entityId === product.entityId,
      ) == null
    ) {
      dispatch(
        setSnackbarInfo({
          isOpen: true,
          message: `Maximum ${config.maxSelectNumber} materials can be selected`,
          status: SnackbarStatusEnum.WARNING,
        }),
      );
    } else {
      dispatch(toggleSelectedMaterial(product));
    }
  };

  const handleOnColorVariantsClick = (product: ProductSearchMaterial) => {
    dispatch(
      setSelectedVariants({
        [product.parentEntityId || '']: product.entityId,
      }),
    );
    pushColorwaysClickAnalytics(product);
  };

  const handleProjectCreate = useCallback(
    (params?: { analyticsEventLocation?: string }) => {
      const { analyticsEventLocation } = params ?? {};

      dispatch(setIsProjectModalOpen(true));

      if (analyticsEventLocation) {
        dispatch(setProjectModalEventLocation(analyticsEventLocation));
      }
    },
    [],
  );

  const onProjectSelect = useCallback((projectId: number) => {
    setCurrentProjectId(projectId);
  }, []);

  const handleProjectClick = (
    projectId: number,
    options?: { analyticsEventLocation?: string },
  ) => {
    const { analyticsEventLocation } = options ?? {};

    const project = activeProjects[projectId];

    if (project) {
      if (!isProjectDataComplete(project)) {
        projectsEditDialogConfig.openProjectEditDialog({
          projectId: project.externalId,
          eventLocation: analyticsEventLocation,
          successCallback(project: ProjectModel) {
            onProjectSelect(project.externalId);
          },
        });
      } else {
        setCurrentProjectId(project.externalId);

        mitGTM.sendEvent({
          event: AnalyticsEventName.SET_PROJECT,
          event_location: analyticsEventLocation,
          action: 'Select',
        });
      }
    }
  };

  const updateLocalStorage = useCallback(
    debounce(() => {
      sessionStorage.setItem('ssScroll', String(window.scrollY));
    }, 500),
    [],
  );

  const handleScroll = () => {
    updateLocalStorage();
  };

  const { id: itemListId, title: itemListName } = useMemo(() => {
    return getTaxonomyAnalyticsDataFromFilters(appFilters);
  }, [appFilters]);

  const handleViewDataLayer = (products: ProductSearchMaterial[]) => {
    const currentColorsList = currentSelectedColors?.join(', ');
    pushSearchAnalyticsDataLayer({
      payload: products,
      currentColor: currentColorsList,
      listId: itemListId,
      listTitle: itemListName,
      eventName: 'view_item_list',
      eventLocation: '',
      isFromCatalog: true,
      isSearch: isSearchRedirect,
    });
  };

  const { onProductInViewChange } = useViewScrollableProductsListEvent({
    sendAnalyticsEvent: handleViewDataLayer,
  });

  const handleSelectDataLayer = (product: ProductSearchMaterial) => {
    const dataLayerAnalytics = product.analyticsData?.dataLayerAnalytics;

    const listId =
      dataLayerAnalytics?.listId ??
      (product?.isDigital ? 'digital_sampling' : itemListId);
    const listTitle =
      dataLayerAnalytics?.listName ??
      (product?.isDigital ? 'Digital sampling' : itemListName);
    const eventLocation =
      dataLayerAnalytics?.eventLocation ??
      (isBannersSearch ? 'Collection' : 'Catalog');

    const currentColorsList = currentSelectedColors?.join(', ');
    pushSearchAnalyticsDataLayer({
      payload: [product],
      currentColor: currentColorsList,
      listId,
      listTitle,
      eventName: 'select_item',
      eventLocation,
    });
  };

  const handleProductClick = async (
    product: ProductSearchMaterial,
    _index = 0,
    withAnalytics = true,
  ) => {
    // Analytics
    handleSelectDataLayer(product);
    if (withAnalytics) {
      const itemProps = getEventsMaterialProps(product);
      await sendAnalytics(
        { id: [String(product?.entityId)], type: 'view', ...itemProps },
        1200,
      );
    }

    if (!isMobile) {
      window.dispatchEvent(
        new CustomEvent('initMiniPDP', {
          detail: {
            id: product.entityId,
            product,
            isProduct: product.isProducts,
            isMaterial: !product.isProducts,
            clickedProductGridIndex: product.gridIndex,
            clickedProductRequestId: product.originRequestId || xRequestId,
            clickedProductAnalyticsClass: product.analyticsData?.analyticsClass,
            clickedProductAnalyticsImpressions:
              product.analyticsData?.impressions,
            dataLayerAnalytics: product.analyticsData?.dataLayerAnalytics,
          },
        }),
      );
    } else {
      window.location.href = product.url;
    }
  };

  const handleContextMenuClick = (product: ProductSearchMaterial) => {
    handleSelectDataLayer(product);
  };

  const handleAddToCart = ({
    product,
    productEventData,
  }: ProductCartClickProps) => {
    handleAddToBag(
      {
        product,
        productEventData,
        isMobile,
        analyticsEventLocation: isBannersSearch
          ? AnalyticsEventLocation.COLLECTION
          : AnalyticsEventLocation.CATALOG,
        relatedProductData: undefined,
        shouldTriggerRecModal: undefined,
        additionalSplitProperties: {},
        listId: itemListId,
        listName: itemListName,
      },
      currentProject,
    );
  };

  const handleBoards = async (
    product: ProductSearchMaterial,
    index: number,
  ) => {
    await handleAddToBoards({
      product,
      index,
      itemListId,
      itemListName,
      analyticsEventLocation: isBannersSearch
        ? AnalyticsEventLocation.COLLECTION
        : AnalyticsEventLocation.CATALOG,
    });
  };

  const cartItemsIds = useMemo(() => {
    return cartItems?.map((item: CartItem) => item.entityId) || [];
  }, [cartItems]);

  const renderItem = (index: number) => {
    const rawMaterial = results.slice(0, results.length)[index];
    const indexByPage =
      ((pagination?.currentPage ?? 1) - 1) * (pagination?.perPage ?? 0);
    const material = {
      ...rawMaterial,
      gridIndex: indexByPage + index + 1,
    };
    if (material == null) {
      return null;
    }
    const isMultiSelected =
      (multiselectIds ?? []).find(
        (id: string) =>
          id === material?.entityId ||
          id === material?.selectedColorFilterEntityId,
      ) != null;

    const isCompareLimitReached =
      isComparisonActive && comparisonItems?.length >= 5;
    const isSelectDisabled = !isMultiSelected && isCompareLimitReached;

    return (
      <ResultCardWrapper
        isVirtualized
        shouldShowVariants
        key={material?.sku}
        cardStaticLabels={cardStaticLabels}
        isSearchPage={isCuratedCollectionPage() || isSearch}
        material={material}
        searchOptions={searchOptions}
        searchToggle={searchToggle}
        isMultiselect={isMultiselect || isComparisonActive}
        isSelected={isMultiSelected}
        onMaterialSelect={handleMaterialSelect}
        multiselectIds={multiselectIds}
        imageSize={defaultImageSize}
        projects={structuredProjects}
        selectedProject={currentProjectData}
        cartItemsIds={cartItemsIds}
        onProductClick={handleProductClick}
        onCreateProjectClick={handleProjectCreate}
        onProjectClick={handleProjectClick}
        isMobile={isMobile}
        isSmallDesktop={isSmallDesktop}
        onContextMenuClick={handleContextMenuClick}
        itemListId={itemListId}
        itemListName={itemListName}
        analyticsEventLocation={
          isBannersSearch
            ? AnalyticsEventLocation.COLLECTION
            : AnalyticsEventLocation.CATALOG
        }
        onInViewChange={(itemInView) =>
          onProductInViewChange(material, itemInView)
        }
        onProjectSelect={onProjectSelect}
        userLang={userLang}
        selectedVariants={selectedVariants}
        additionalSplitProperties={{}}
        onColorVariantsClick={handleOnColorVariantsClick}
        isSelectboxDisabled={isSelectDisabled}
        isCompareLimitReached={isCompareLimitReached}
        onAddToCart={handleAddToCart}
        onAddToFavorites={handleBoards}
        loadingCartItemIds={loadingIds}
      />
    );
  };

  const noResultsFound = results.length === 0 && !isSearching;
  const shouldShowDefaultGrid = isPageZoomed && isSearchPage();

  if (isBannersSearch && results.length === 0) {
    return <Fragment />;
  }

  if (noResultsFound) {
    return <EmptyResult intl={intl} />;
  }

  const withMargin =
    campaignContentBanner != null && results?.length < 25 && !isPartnersSearch;

  return (
    <Styled.ResultsWrapper withMargin={withMargin}>
      <div ref={gridContainerRef} id="searchScrollContainer">
        <Styled.ResultsList
          ref={resultContainerRef}
          isDefaultGrid={shouldShowDefaultGrid}
        >
          {isSearching ? (
            <GridSkeleton itemsNumber={20} />
          ) : shouldShowDefaultGrid ? (
            <>
              {results?.map((item, index: number) => (
                <ItemContainer
                  key={item.entityId}
                  dataIndex={index}
                  contentBanner={campaignContentBanner}
                  currentPage={pagination?.currentPage || 1}
                  searchResultsLength={results.length}
                  contentBlocksLength={0}
                >
                  {renderItem(index)}
                </ItemContainer>
              ))}
            </>
          ) : (
            <VirtuosoGrid
              useWindowScroll
              overscan={100}
              style={{ height: window.innerHeight }}
              totalCount={results.length}
              components={{
                Item: (props) => (
                  <ItemContainer
                    {...props}
                    contentBanner={campaignContentBanner}
                    currentPage={pagination?.currentPage || 1}
                    searchResultsLength={results.length}
                    contentBlocksLength={0}
                  />
                ),
              }}
              itemContent={renderItem}
              id="result-container"
            />
          )}
        </Styled.ResultsList>
      </div>
      <div>
        {campaignBottomBanner != null && (
          <CampaignBanner {...campaignBottomBanner} />
        )}
      </div>
      {(!isSearchLite || isBannersSearch) && (
        <div id="paginationWrapper">
          {pagination?.totalPages && pagination.totalPages > 1 && (
            <Styled.PaginationWrapper>
              <Pagination
                count={pagination?.totalPages}
                page={pagination?.currentPage}
                onChange={handleNextPage}
              />
            </Styled.PaginationWrapper>
          )}
        </div>
      )}
    </Styled.ResultsWrapper>
  );
}, areComponentPropsEqual);

MaterialsContainer.displayName = 'MaterialsContainer';

export default MaterialsContainer;
